import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { BooleanTypes, ColumnTypeCellRendererComponent, DateTypes, NumberTypes } from '@y42/column-type';
import { ListViewComponent, provideSimpleListView, QuickFilterModel } from '@y42/core/list-view';
import { ColumnTypeEnum } from '@y42/json-schema';
import { QuickFilterLocationEnum } from '@y42/model';
import {
  ColDef,
  ColumnState,
  ColumnStateParams,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  RowClickedEvent,
} from 'ag-grid-community';
import { delay, filter, map, Observable, take, tap } from 'rxjs';
import { FunctionDefinitionModel } from '../../models/function-definition.model';
import { FunctionDetailsRendererComponent } from './components/function-details-renderer/function-details-renderer.component';
import { FunctionTagRendererComponent } from './components/function-tag-renderer/function-tag-renderer.component';
import { FunctionListViewService } from './function-list-view-service';

@UntilDestroy()
@Component({
  selector: 'y42-function-list-view',
  templateUrl: './function-list-view.component.html',
  styleUrls: ['./function-list-view.component.scss'],
  providers: [provideSimpleListView(FunctionListViewService)],
})
export class FunctionListViewComponent {
  constructor(private readonly functionListViewService: FunctionListViewService) {}

  @ViewChild('listView') listView?: ListViewComponent;

  private gridApi!: GridApi;

  private readonly defaultQuickFilters$: Observable<QuickFilterModel[]> =
    this.functionListViewService.columnTypes$.pipe(
      map(() => {
        const numbericTypeDef = this.functionListViewService.getTypeDefinition(ColumnTypeEnum.Numeric);
        const integerTypeDef = this.functionListViewService.getTypeDefinition(ColumnTypeEnum.Integer);
        const floatTypeDef = this.functionListViewService.getTypeDefinition(ColumnTypeEnum.Float);
        const stringTypeDef = this.functionListViewService.getTypeDefinition(ColumnTypeEnum.String);
        const booleanTypeDef = this.functionListViewService.getTypeDefinition(ColumnTypeEnum.Boolean);
        const dateTypeDef = this.functionListViewService.getTypeDefinition(ColumnTypeEnum.Date);

        const baseOptions: () => ColumnStateParams = () => ({
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          pivot: false,
          pivotIndex: null,
          flex: 1,
        });

        const firstInputTypeOptions: (params: {
          hide: boolean;
          rowGroup: boolean;
          rowGroupIndex: null | number;
          width: number;
        }) => ColumnState = (params) => ({
          colId: 'first_input_type',
          ...baseOptions(),
          ...params,
        });

        const parameterLengthOptions: () => ColumnState = () => ({
          colId: 'parameter_length',
          ...baseOptions(),
          width: 60,
          hide: false,
          rowGroup: false,
          rowGroupIndex: null,
        });

        const tagOptions: (params: {
          hide: boolean;
          rowGroup: boolean;
          rowGroupIndex: null | number;
          width: number;
        }) => ColumnState = (params) => ({
          colId: 'tag',
          ...baseOptions(),
          ...params,
        });

        const functionNameOptions: () => ColumnState = () => ({
          colId: 'function_name',
          ...baseOptions(),
          sort: 'asc',
          sortIndex: 0,
          width: 164.79700000000003,
          hide: false,
          rowGroup: false,
          rowGroupIndex: null,
        });

        const operatorOptions: () => ColumnState = () => ({
          colId: 'operator',
          ...baseOptions(),
          width: 70,
          hide: false,
          rowGroup: false,
          rowGroupIndex: null,
        });

        const outputOptions: () => ColumnState = () => ({
          colId: 'output',
          ...baseOptions(),
          width: 70,
          hide: false,
          rowGroup: false,
          rowGroupIndex: null,
        });

        const descriptionOptions: () => ColumnState = () => ({
          colId: 'description',
          ...baseOptions(),
          width: 200,
          hide: false,
          rowGroup: false,
          rowGroupIndex: null,
        });

        const agGridAutoColumnOptions: () => ColumnState = () => ({
          colId: 'ag-Grid-AutoColumn',
          ...baseOptions(),
          pinned: 'left',
          width: 259,
          hide: false,
          rowGroup: false,
          rowGroupIndex: null,
        });

        const quickFilterModels: QuickFilterModel[] = [
          {
            id: 'cdc70db7-a60f-4c8f-84a6-ac5cb65685a3',
            name: 'Number Functions',
            filterModel: {
              output: {
                values: [...NumberTypes],
                type: 'equals',
              },
            },
            default: false,
            editable: true,
            background_color: numbericTypeDef.color,
            icon: numbericTypeDef.icon as IconName,
            icon_width: 9,
            is_font_awesome: false,
            columnStates: [
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              functionNameOptions(),
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
              agGridAutoColumnOptions(),
            ],
          },
          {
            id: 'cdc70db7-a60f-4c8f-84a6-ac5cb65685in',
            name: 'Integer Functions',
            filterModel: {
              output: {
                values: [ColumnTypeEnum.Integer],
                type: 'equals',
              },
            },
            default: false,
            editable: true,
            background_color: integerTypeDef.color,
            icon: integerTypeDef.icon as IconName,
            icon_width: 9,
            is_font_awesome: false,
            columnStates: [
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              functionNameOptions(),
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
              agGridAutoColumnOptions(),
            ],
          },
          {
            id: 'cdc70db7-a60f-4c8f-84a6-ac5cb65685fl',
            name: 'Float Functions',
            filterModel: {
              output: {
                values: [ColumnTypeEnum.Float],
                type: 'equals',
              },
            },
            default: false,
            editable: true,
            background_color: floatTypeDef.color,
            icon: floatTypeDef.icon as IconName,
            icon_width: 9,
            is_font_awesome: false,
            columnStates: [
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              functionNameOptions(),
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
              agGridAutoColumnOptions(),
            ],
          },
          {
            id: 'cdc70db7-a60f-4c8f-84a6-adaskkj23',
            name: 'Text Functions',
            filterModel: {
              output: {
                values: [ColumnTypeEnum.String, ColumnTypeEnum.ArrayString],
                type: 'equals',
              },
            },
            default: false,
            editable: true,
            background_color: stringTypeDef.color,
            icon: stringTypeDef.icon as IconName,
            icon_width: 9,
            is_font_awesome: false,
            columnStates: [
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              functionNameOptions(),
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
              agGridAutoColumnOptions(),
            ],
          },
          {
            id: 'boolean',
            name: 'Boolean Functions',
            filterModel: {
              output: {
                values: [...BooleanTypes],
                type: 'equals',
              },
            },
            default: false,
            editable: true,
            background_color: booleanTypeDef.color,
            icon: booleanTypeDef.icon as IconName,
            icon_width: 9,
            is_font_awesome: false,
            columnStates: [
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              functionNameOptions(),
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
              agGridAutoColumnOptions(),
            ],
          },
          {
            id: 'date_time_stamp',
            name: 'Date + Time Functions',
            filterModel: {
              output: {
                values: [...DateTypes],
                type: 'equals',
              },
            },
            default: false,
            editable: true,
            background_color: dateTypeDef.color,
            icon: dateTypeDef.icon as IconName,
            icon_width: 9,
            is_font_awesome: false,
            columnStates: [
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              functionNameOptions(),
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
              agGridAutoColumnOptions(),
            ],
          },
          {
            id: 'b8b2ae4e-77b2-4ecf-bc8c-934e23facd2d',
            name: 'white space',
            filterModel: {
              tag: {
                values: ['text whitespace'],
                type: 'equals',
              },
            },
            default: true,
            editable: false,
            background_color: '#fcfcfc',
            icon: 'transporter-empty' as IconName,
            is_font_awesome: true,
            columnStates: [
              firstInputTypeOptions({ width: 253, rowGroup: false, rowGroupIndex: null, hide: false }),
              parameterLengthOptions(),
              tagOptions({ width: 253, rowGroup: false, rowGroupIndex: null, hide: false }),
              {
                ...functionNameOptions(),
                sort: null,
                sortIndex: null,
                width: 253,
              },
              operatorOptions(),
              outputOptions(),
              {
                ...descriptionOptions(),
                width: 252,
              },
            ],
          },
          {
            id: 'f4190d11-6030-489b-a2e2-9e98aa0852b7',
            name: 'NULL values',
            filterModel: {
              function_name: {
                type: 'contains',
                filter: 'Null',
              },
            },
            default: true,
            editable: false,
            background_color: '#fcfcfc',
            icon: 'skull' as IconName,
            is_font_awesome: true,
            columnStates: [
              {
                ...agGridAutoColumnOptions(),
                width: 323,
              },
              firstInputTypeOptions({ width: 100, rowGroup: true, rowGroupIndex: 0, hide: true }),
              parameterLengthOptions(),
              tagOptions({ width: 200, rowGroup: true, rowGroupIndex: 1, hide: true }),
              {
                ...functionNameOptions(),
                sort: null,
                sortIndex: null,
                width: 100,
              },
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
            ],
          },
          {
            id: '31f84708-52cf-4a2c-8b9c-f865bbd009f2',
            name: 'Group By Output',
            filterModel: {},
            default: true,
            editable: false,
            background_color: '#fcfcfc',
            icon: 'sign-out-alt' as IconName,
            is_font_awesome: true,
            columnStates: [
              {
                ...agGridAutoColumnOptions(),
                width: 130,
                flex: 1,
              },
              firstInputTypeOptions({ width: 100, rowGroup: false, rowGroupIndex: null, hide: false }),
              parameterLengthOptions(),
              {
                ...tagOptions({ width: 200, rowGroup: false, rowGroupIndex: null, hide: false }),
                flex: null,
              },
              {
                ...functionNameOptions(),
                sort: null,
                sortIndex: null,
                width: 216,
                flex: null,
              },
              operatorOptions(),
              {
                ...outputOptions(),
                hide: true,
                rowGroup: true,
                rowGroupIndex: 0,
              },
              {
                ...descriptionOptions(),
                width: 871,
                flex: null,
              },
            ],
          },
          {
            id: 'd748ae52-6f36-41c4-ad9f-5f09925bbce7',
            name: 'Operator',
            filterModel: {
              tag: {
                values: ['operator'],
                type: 'equals',
              },
            },
            default: true,
            editable: false,
            background_color: '#fcfcfc',
            icon: 'less-than-equal' as IconName,
            is_font_awesome: true,
            columnStates: [
              firstInputTypeOptions({ width: 118, rowGroup: false, rowGroupIndex: null, hide: false }),
              parameterLengthOptions(),
              {
                ...tagOptions({ width: 150, rowGroup: false, rowGroupIndex: null, hide: false }),
                sort: 'asc',
                sortIndex: 0,
              },
              {
                ...functionNameOptions(),
                sort: null,
                sortIndex: null,
                width: 118,
              },
              operatorOptions(),
              outputOptions(),
              descriptionOptions(),
            ],
          },
        ];

        return quickFilterModels;
      }),
      untilDestroyed(this),
    );

  public readonly state = connectState(this, {
    defaultQuickFilters: this.defaultQuickFilters$,
  });

  // quick Filter
  public readonly quickFilterKey = `${QuickFilterLocationEnum.Function}`;

  // grid options
  public readonly gridOptions: GridOptions = {
    enableGroupEdit: true,
    rowHeight: 35,
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: false,
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      minWidth: 100,
    },
    autoGroupColumnDef: {
      minWidth: 130,
      maxWidth: 350,
      filter: 'agTextColumnFilter',
      flex: 1,
      menuTabs: [],
      pinned: 'left',
      /*filterValueGetter: (params) => {
				return params.api.getValue(
					params.colDef.showRowGroup,
					params.node
				);
			},*/
    },
    detailCellRenderer: 'functionDetailsRenderer',
    detailRowHeight: 300,
    masterDetail: true,
    embedFullWidthRows: true,
    groupDisplayType: 'singleColumn',
    suppressCellFocus: true,
    headerHeight: 30,
    rowClass: 'cursor-pointer',
    animateRows: true,
    rowGroupPanelShow: 'always',
    getContextMenuItems: this.getContextMenuItems.bind(this),
  };

  public readonly columnDefs: ColDef[] = [
    {
      headerName: 'Main Input Type (1st)',
      field: 'first_input_type',
      cellRenderer: 'columnTypeRenderer',
      rowGroup: true,
      filter: 'agSetColumnFilter',
      hide: true,
      enableRowGroup: true,
      width: 90,
    },
    {
      headerName: 'Inputs',
      field: 'parameter_length',
      filter: 'agNumberColumnFilter',
      enableRowGroup: true,
      maxWidth: 60,
      valueFormatter(params) {
        if (!params.node?.group) {
          return `${params.value}x`;
        }
        return params.value;
      },
    },
    {
      headerName: 'Tag',
      field: 'tag',
      rowGroup: true,
      cellRenderer: 'functionTagRenderer',
      filter: 'agSetColumnFilter',
      hide: true,
      minWidth: 150,
      enableRowGroup: true,
    },
    {
      headerName: 'Name',
      field: 'function_name',
      filter: 'agTextColumnFilter',
      sort: 'asc',
      sortIndex: 0,
      width: 90,
      cellClass: 'font-medium text-gray-1100',
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      headerName: 'Operator',
      field: 'operator',
      filter: 'agSetColumnFilter',
      maxWidth: 70,
    },
    {
      headerName: 'Output',
      field: 'output',
      cellRenderer: 'columnTypeRenderer',
      filter: 'agSetColumnFilter',
      maxWidth: 70,
      enableRowGroup: true,
    },
    {
      headerName: 'Description',
      field: 'description',
      filter: 'agTextColumnFilter',
      minWidth: 200,
    },
  ];

  public readonly frameworkComponents = {
    columnTypeRenderer: ColumnTypeCellRendererComponent,
    functionDetailsRenderer: FunctionDetailsRendererComponent,
    functionTagRenderer: FunctionTagRendererComponent,
  };

  @Input() set showAggregateFunctions(show: boolean) {
    this.functionListViewService.setShowAggregateFunctions(show);
  }

  @Output() addFunction: EventEmitter<string> = new EventEmitter();

  resetView(): void {
    this.gridApi?.expandAll();
  }

  filterSet() {
    this.gridApi?.expandAll();
  }

  gridReady({ api }: GridOptions) {
    if (api) {
      this.gridApi = api;
    }

    this.functionListViewService.data$
      .pipe(
        filter((data) => !!data),
        tap((data) => console.log('functionListViewService data', data)),
        take(1),
        delay(500),
        tap(() => this.gridApi?.expandAll()),
        untilDestroyed(this),
      )
      .subscribe();
  }

  onRowClicked(event: RowClickedEvent) {
    const selectedRows = this.gridApi?.getSelectedRows() || [];
    const foundIndex = selectedRows.findIndex((row) => row.function_name === event.data.function_name);
    if (foundIndex !== -1) {
      this.gridApi?.deselectAll();
    }
  }

  expandFunction(functionName: string) {
    this.listView?.resetView();
    this.gridApi?.forEachNode((node) => {
      if (node && node.data && node.data.function_name === functionName) {
        if (!node.group) {
          if (node?.parent) {
            node.parent.setExpanded(true);
            if (node.parent?.group) {
              node.parent?.parent?.setExpanded(true);
            }
          }
        }
        node.setSelected(true);
        this.gridApi?.ensureNodeVisible(node, 'top');
        this.gridApi?.setRowNodeExpanded(node, true);
      }
    });
  }

  getContextMenuItems(params: GetContextMenuItemsParams) {
    return [
      'copy',
      'expandAll',
      'contractAll',
      'export',
      {
        name: 'Use Function in Editor',
        action: (() => {
          let data: FunctionDefinitionModel;
          if (params.node?.group) {
            data = params.node.allLeafChildren[0]?.data;
          } else {
            data = params.node?.data;
          }
          this.addFunction.emit(data.function_name);
        }).bind(this),
        cssClasses: ['redFont', 'bold'],
        /*icon: '<img src="../images/skills/windows.png"/>',*/
      },
    ];
  }
}
